import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Clock, Plus, AlertCircle, CheckCircle, Pencil, Trash2, X, Save, Loader2 } from 'lucide-react';
import { canUserEdit, canUserDelete } from '@/utils/permissions';

const PAGE_PATH = '/projects/upcoming';

const emptyForm = {
  name: '',
  description: '',
  projectIncharge: '',
  plannedStartDate: '',
  plannedDeadline: '',
  estimatedCost: '',
  priority: 'Medium',
  approvalStatus: 'Pending',
  remarks: '',
};

function UpcomingProjects() {
  const { user, API } = useContext(AppContext);
  const canEdit = canUserEdit(user, PAGE_PATH);
  const canDelete = canUserDelete(user, PAGE_PATH);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects/upcoming`);
      setProjects(res.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || '',
      description: p.description || '',
      projectIncharge: p.projectIncharge || '',
      plannedStartDate: p.plannedStartDate || '',
      plannedDeadline: p.plannedDeadline || '',
      estimatedCost: p.estimatedCost ?? '',
      priority: p.priority || 'Medium',
      approvalStatus: p.approvalStatus || 'Pending',
      remarks: p.remarks || '',
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { alert('Project name is required.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, estimatedCost: parseFloat(form.estimatedCost) || 0 };
      if (editingId) {
        await axios.put(`${API}/projects/upcoming/${editingId}`, payload);
      } else {
        await axios.post(`${API}/projects/upcoming`, payload);
      }
      setShowModal(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchProjects();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete upcoming project "${p.name}"? This cannot be undone.`)) return;
    setDeletingId(p.id);
    try {
      await axios.delete(`${API}/projects/upcoming/${p.id}`);
      setProjects((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-700 border-red-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getApprovalColor = (status) => {
    switch (status) {
      case 'Approved': return 'bg-green-100 text-green-700';
      case 'Pending': return 'bg-yellow-100 text-yellow-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) return <div className="text-center py-12">Loading upcoming projects...</div>;

  return (
    <div className="space-y-6" data-testid="upcoming-projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upcoming Projects</h1>
          <p className="text-gray-500 mt-1">Pipeline projects awaiting execution</p>
        </div>
        {canEdit && (
          <button
            onClick={openCreate}
            className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md hover:opacity-90"
            data-testid="propose-project-btn"
          >
            <Plus size={18} />
            <span>Propose Project</span>
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <div className="flex items-center space-x-2"><Clock className="text-blue-500" size={18} /><p className="text-sm text-blue-600 font-medium">Total Planned</p></div>
          <p className="text-2xl font-bold text-blue-700">{projects.length}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2"><CheckCircle className="text-green-500" size={18} /><p className="text-sm text-green-600 font-medium">Approved</p></div>
          <p className="text-2xl font-bold text-green-700">{projects.filter(p => p.approvalStatus === 'Approved').length}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-5 border border-yellow-200">
          <div className="flex items-center space-x-2"><AlertCircle className="text-yellow-600" size={18} /><p className="text-sm text-yellow-700 font-medium">Pending Approval</p></div>
          <p className="text-2xl font-bold text-yellow-700">{projects.filter(p => p.approvalStatus === 'Pending').length}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Estimated Investment</p>
          <p className="text-2xl font-bold text-gray-900">₹{(projects.reduce((s, p) => s + (p.estimatedCost || 0), 0) / 10000000).toFixed(1)} Cr</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Project ID</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Project Name</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Planned Start</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Target Deadline</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Est. Cost</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Priority</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Approval</th>
                {(canEdit || canDelete) && (
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {projects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50 transition-colors" data-testid={`upcoming-row-${project.id}`}>
                  <td className="px-4 py-4 font-mono text-sm text-[#1a237e] font-medium">{project.id}</td>
                  <td className="px-4 py-4">
                    <p className="text-sm font-medium text-gray-900">{project.name}</p>
                    <p className="text-xs text-gray-500 truncate max-w-xs">{project.description}</p>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">{project.plannedStartDate}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{project.plannedDeadline}</td>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-900">₹{((project.estimatedCost || 0) / 100000).toFixed(0)} L</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getPriorityColor(project.priority)}`}>{project.priority}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getApprovalColor(project.approvalStatus)}`}>{project.approvalStatus}</span>
                  </td>
                  {(canEdit || canDelete) && (
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && (
                          <button
                            onClick={() => openEdit(project)}
                            className="p-1.5 text-blue-700 hover:bg-blue-50 rounded"
                            title="Edit"
                            data-testid={`edit-upcoming-${project.id}`}
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(project)}
                            disabled={deletingId === project.id}
                            className="p-1.5 text-rose-600 hover:bg-rose-50 rounded disabled:opacity-50"
                            title="Delete"
                            data-testid={`delete-upcoming-${project.id}`}
                          >
                            {deletingId === project.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {projects.length === 0 && (
          <div className="text-center py-12 text-gray-500">No upcoming projects yet.</div>
        )}
      </div>

      {/* Remarks Section */}
      {projects.some((p) => p.remarks) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.filter((p) => p.remarks).map((project) => (
            <div key={project.id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">{project.name}</h3>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(project.priority)}`}>{project.priority}</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{project.description}</p>
              <div className="bg-yellow-50 p-3 rounded-lg">
                <p className="text-xs text-yellow-800 font-medium mb-1">Remarks:</p>
                <p className="text-sm text-yellow-700">{project.remarks}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="upcoming-modal">
          <form onSubmit={handleSave} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-[#1a237e] to-[#283593] rounded-t-2xl">
              <h2 className="text-lg font-bold text-white">{editingId ? 'Edit Upcoming Project' : 'Propose Upcoming Project'}</h2>
              <button type="button" onClick={() => setShowModal(false)} className="p-2 text-white/80 hover:bg-white/10 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Project Name *</label>
                <input type="text" required value={form.name}
                       onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                       className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                       data-testid="upcoming-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <textarea rows="2" value={form.description}
                          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Project In-charge</label>
                  <input type="text" value={form.projectIncharge}
                         onChange={(e) => setForm((p) => ({ ...p, projectIncharge: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Estimated Cost (₹)</label>
                  <input type="number" step="0.01" value={form.estimatedCost}
                         onChange={(e) => setForm((p) => ({ ...p, estimatedCost: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Planned Start Date</label>
                  <input type="date" value={form.plannedStartDate}
                         onChange={(e) => setForm((p) => ({ ...p, plannedStartDate: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Target Deadline</label>
                  <input type="date" value={form.plannedDeadline}
                         onChange={(e) => setForm((p) => ({ ...p, plannedDeadline: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Priority</label>
                  <select value={form.priority}
                          onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Approval Status</label>
                  <select value={form.approvalStatus}
                          onChange={(e) => setForm((p) => ({ ...p, approvalStatus: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Remarks</label>
                <textarea rows="2" value={form.remarks}
                          onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
              <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] disabled:opacity-60"
                      data-testid="upcoming-save-btn">
                <Save size={16} /> {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default UpcomingProjects;
